import { useEffect, useState } from 'react';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';

/**
 * Google Maps JS の遅延ロード (docs/25 §7-2/§11、Increment 3)。
 *
 * `@googlemaps/js-api-loader` の新 functional API (`setOptions`+`importLibrary`) を使用
 * (旧 `Loader` クラスは deprecated)。district の `streetView.enabled` が true かつ、実際に
 * 座標選択/3Dパノラマ表示を試みた瞬間にのみロードする (Street View dynamic 課金を避けるため
 * 常時ロードはしない)。
 *
 * 必要なライブラリ: maps (Map基盤), marker (legacy Marker/SymbolPath), streetView
 * (StreetViewService/StreetViewPanorama), geometry (spherical.computeOffset)。
 */

const REQUIRED_LIBRARIES = ['maps', 'marker', 'streetView', 'geometry'] as const;

let _loadedApiKey: string | null = null;
let _loadPromise: Promise<void> | null = null;

/** module-level singleton: 同じ apiKey なら再ロードせず既存 Promise を返す。 */
export function loadGoogleMaps(apiKey: string): Promise<void> {
  if (_loadPromise && _loadedApiKey === apiKey) return _loadPromise;
  _loadedApiKey = apiKey;
  setOptions({ key: apiKey, v: 'weekly' });
  _loadPromise = Promise.all(REQUIRED_LIBRARIES.map((lib) => importLibrary(lib))).then(
    () => undefined,
  );
  return _loadPromise;
}

export type GoogleMapsLoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready' }
  | { status: 'error'; error: string };

export function useGoogleMapsLoader(apiKey: string | null): GoogleMapsLoadState {
  const [state, setState] = useState<GoogleMapsLoadState>({ status: 'idle' });

  useEffect(() => {
    if (!apiKey) {
      setState({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    loadGoogleMaps(apiKey)
      .then(() => {
        if (!cancelled) setState({ status: 'ready' });
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setState({ status: 'error', error: e instanceof Error ? e.message : String(e) });
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  return state;
}
